// MagentaTabBar — the segmented pill row, parameterised by accent.
//
// Lifted from the player /rewards page tab pattern: a thin rounded-2xl
// container, flex-1 buttons, active = solid accent fill, inactive =
// quiet muted text. Used as:
//   - top sub-tabs on the Players hub  (Player requests | Players)
//   - top sub-tabs on the Agents hub   (Agent requests | Agents)
//   - the Recharge | Withdrawal hero toggle
//   - the Pending | History sub-strip
//
// Accent options (controls the active pill colour + glow):
//   'brand'    — magenta, used by the global chrome (default)
//   'sky'      — Players section colour (matches bottom-nav bubble)
//   'emerald'  — Agents section colour  (matches bottom-nav bubble)
//   'indigo'   — Profile section colour (matches bottom-nav bubble)
//   'violet'   — Rewards hub colour     (Phase 5)
//
// Two modes:
//   state-driven  — pass `activeKey` + `onChange`, items use `key`
//   route-driven  — items use `to`, NavLink semantics handle active
//
// Sizes:
//   'lg' — page-width hero (Recharge / Withdrawal, top sub-tabs)
//   'sm' — compact (Pending / History)

import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import Icon from '../../components/Icon';

// Phase 8 (2026-05-11): added 'yellow' accent for the renamed Rewards
// hub. The active pill uses amber-500 (not raw yellow) so the white
// text stays readable on the warm fill in light mode; the cooler
// yellow-300 dark-mode bubble in the bottom nav is paired here with
// a slightly deeper amber-500 fill so the segmented bar reads as the
// same family without bleaching the text. 'violet' is kept for
// backward compatibility but is no longer used by the agent app.
export type Accent = 'brand' | 'sky' | 'emerald' | 'indigo' | 'violet' | 'yellow';

const ACCENT_ACTIVE: Record<Accent, string> = {
  brand:   'bg-brand-500 text-white shadow-[0_2px_10px_rgba(var(--brand-rgb),0.35)]',
  sky:     'bg-sky-500 text-white shadow-[0_2px_10px_rgba(14,165,233,0.4)]',
  emerald: 'bg-emerald-500 text-white shadow-[0_2px_10px_rgba(16,185,129,0.4)]',
  indigo:  'bg-indigo-500 text-white shadow-[0_2px_10px_rgba(99,102,241,0.4)]',
  violet:  'bg-violet-500 text-white shadow-[0_2px_10px_rgba(139,92,246,0.4)]',
  yellow:  'bg-amber-500 text-white shadow-[0_2px_10px_rgba(245,158,11,0.4)]',
};

export type MagentaTabItem = {
  /** Route-driven mode. */
  to?: string;
  /** State-driven mode. */
  key?: string;
  label: string;
  icon?: string;
  /** Optional count badge — renders as a magenta pill with the number. */
  count?: number;
  /** Phase R7 follow-up (2026-05-14): optional bare-emoji indicator.
   *  Used for non-numeric alerts (😨 wallet-negative, 😳 deposit-zero).
   *  Rendered as a chrome-less glyph at the same footprint as the
   *  count badge so the tab silhouette stays consistent. Has an
   *  attention-grabbing wiggle+pulse animation. Coexists with `count`
   *  when both apply (count sits to the LEFT of the glyph). */
  glyph?: string;
  /** ARIA label describing what the glyph means (for screen readers). */
  glyphAriaLabel?: string;
  /** Match URLs starting with this prefix as active (route-driven). */
  matchPrefix?: string;
  /** Hide tab if false. */
  show?: boolean;
};

type Props = {
  items: MagentaTabItem[];
  size?: 'lg' | 'sm';
  /** Active pill colour. Defaults to 'brand' for legacy call sites. */
  accent?: Accent;
  /** State-driven only. */
  activeKey?: string;
  onChange?: (key: string) => void;
  className?: string;
};

const SIZE_CLS = {
  lg: 'py-2.5 text-[13px] sm:text-sm gap-1.5',
  sm: 'py-1.5 text-[12px] gap-1',
};

export default function MagentaTabBar({
  items, size = 'lg', accent = 'brand', activeKey, onChange, className = '',
}: Props) {
  const loc = useLocation();
  const visible = items.filter((i) => i.show !== false);

  return (
    <div
      className={`flex gap-1 p-1 rounded-2xl
                  bg-white/70 dark:bg-arena-800/60
                  border border-arena-200/60 dark:border-arena-600/40
                  ${className}`}
    >
      {visible.map((it) => {
        // Route-driven
        if (it.to) {
          const active = it.matchPrefix
            ? loc.pathname === it.to || loc.pathname.startsWith(it.matchPrefix + '/')
            : loc.pathname === it.to;
          return <Item key={it.to} it={it} active={active} size={size} accent={accent} />;
        }
        // State-driven
        const active = it.key === activeKey;
        return (
          <Item
            key={it.key || it.label}
            it={it}
            active={active}
            size={size}
            accent={accent}
            onClick={() => it.key && onChange?.(it.key)}
          />
        );
      })}
    </div>
  );
}

function Item({
  it, active, size, accent, onClick,
}: {
  it: MagentaTabItem;
  active: boolean;
  size: 'lg' | 'sm';
  accent: Accent;
  onClick?: () => void;
}) {
  const cls = `flex-1 inline-flex items-center justify-center font-bold rounded-xl transition active:scale-[0.98]
               ${SIZE_CLS[size]}
               ${active
                 ? ACCENT_ACTIVE[accent]
                 : 'text-arena-700 dark:text-neon-200/70 hover:bg-white/40 dark:hover:bg-arena-700/40'}`;
  const inner = (
    <>
      {it.icon && <Icon name={it.icon} size={size === 'lg' ? 16 : 14} filled />}
      <span>{it.label}</span>
      {/* Phase R7 follow-up (2026-05-14): bare-emoji indicator.
          Same 17×17 footprint as the count badge so the tab keeps a
          stable silhouette, but renders the glyph edge-to-edge with
          NO background pill (a background shrinks the emoji to where
          it's unreadable on a tab strip). Drop-shadow gives a bit of
          separation; wiggle+pulse pulls the eye. */}
      {it.glyph && (
        <motion.span
          initial={{ scale: 0, opacity: 0, rotate: 0 }}
          animate={{
            opacity: 1,
            scale:  [1, 1.22, 0.95, 1.18, 1],
            rotate: [0, -12, 10, -6, 6, 0],
          }}
          transition={{
            opacity: { duration: 0.2 },
            scale:  { duration: 1.1, repeat: Infinity, ease: 'easeInOut' },
            rotate: { duration: 1.1, repeat: Infinity, ease: 'easeInOut' },
          }}
          aria-label={it.glyphAriaLabel || 'Alert'}
          className="ml-0.5 inline-flex items-center justify-center
                     h-[17px] w-[17px]
                     text-[15px] leading-none
                     select-none
                     drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]
                     dark:drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]"
        >
          {it.glyph}
        </motion.span>
      )}
      {typeof it.count === 'number' && it.count > 0 && (
        // Phase R7 follow-up (2026-05-14): count badge uses the agent
        // app's EXACT brand magenta (#e91e8c → tailwind `brand-500`),
        // matching the /profile/wallet hero so the pill reads as part
        // of the same identity language. Earlier we used tailwind's
        // stock `fuchsia-500` (#d946ef) which leaned purple — visibly
        // different from the wallet hero. The dark-mode look stays the
        // same in both themes (the magenta is meant to pop on every
        // tab background).
        //
        // Edge case: when the tab is ACTIVE and the accent itself is
        // 'brand', a magenta badge would melt into the magenta tab
        // fill — in that one case we invert to a white pill with
        // brand-700 text so the number stays readable.
        <span className={`ml-0.5 inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full text-[10px] font-extrabold tabular-nums
                          ${active && accent === 'brand'
                            ? 'bg-white text-brand-700 ring-1 ring-white/40'
                            : 'bg-brand-500 text-white shadow-[0_1px_4px_rgba(233,30,140,0.55)] ring-1 ring-brand-300/50'}`}>
          {it.count > 99 ? '99+' : it.count}
        </span>
      )}
    </>
  );

  if (it.to) {
    return (
      <NavLink to={it.to} end={it.to === '/'} className={cls}>
        {inner}
      </NavLink>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls}>
      {inner}
    </button>
  );
}
